import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import sortBy from 'lodash.sortby';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Configure } from 'react-instantsearch-dom';
import styled from 'styled-components';

// components
import TextInput from '@atoms/TextInput/index';
import SelectPersonalResponsibilities from '@molecules/SelectPersonalResponsibilities';
import CheckboxWithoutForm from '@atoms/CheckboxWithoutForm';
import AutocompleteCompanyNames from '@molecules/AutocompleteCompanyNames';
import SearchContainer from '@search/SearchContainer';
import { BodySmall, ButtonPrimaryLarge } from '@styled';
import AccountNewCompanyFields from './AccountNewCompanyFields';

// functionality
import useViewer from '@hooks/useViewer';
import useBusinessTypes from '@hooks/useBusinessTypes';
import { convertMemberTypeTextToCode } from '@general/helpers/member.helpers';
import useCompanyFieldsContext from './CompanyFieldsContext';
import { MODES } from './CompanyFieldsReducer';
import { mediaQueries } from '@general/breakpoints';

const StyledInputContainer = styled.div`
    margin-bottom: 1.5rem;
`;

const StyledCompanySelectionContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;

    @media ${mediaQueries.md} {
        align-items: flex-end;
        flex-direction: row;
    }
`;

const StyledNewCompanyButtonContainer = styled.div`
    //display: 'flex', alignItems: 'center', flexDirection: 'row', gap: '1rem'
    display: flex;
    flex-direction: column;
    gap: 1rem;

    @media ${mediaQueries.md} {
        align-items: center;
        flex-direction: row;
    }
`;

const CompanyInformationFields = ({ setIsManualCompanyAddress, setHasSecondaryBusinessTypes }) => {
    //---------------------------------
    // State
    //---------------------------------

    const [primaryBusinessTypeOptions, setPrimaryBusinessTypeOptions] = useState();
    const { errors, setFieldValue, setTouched, touched } = useFormikContext();
    const { viewer } = useViewer();
    const memberType = convertMemberTypeTextToCode(viewer?.memberType);
    const { data: businessTypes } = useBusinessTypes(memberType);
    const filters = 'status:Active';
    const {
        state: fieldsState,
        clearSearchAction,
        toggleIndependentAction,
        setModeAction,
        isFieldDisabled,
        isFieldHidden,
        isFieldActive,
    } = useCompanyFieldsContext();

    const searchInputRef = useRef(null);

    const { independent } = fieldsState;

    //---------------------------------
    // Effects
    //---------------------------------
    useEffect(() => {
        if (!businessTypes) {
            return;
        }

        setPrimaryBusinessTypeOptions(
            sortBy(
                businessTypes.map(b => ({ value: b.code, label: b.description })),
                'label'
            )
        );
    }, [businessTypes]);

    const handleSelect = useCallback(
        option => {
            setModeAction(MODES.EXISTING_SEARCH);
            const companyID = parseInt(option.objectID.split('-').pop());
            setFieldValue('companyID', companyID);
        },
        [setFieldValue, setModeAction]
    );

    if (!primaryBusinessTypeOptions) {
        return null;
    }

    return (
        <div>
            {isFieldActive('memberFields') && (
                <div className="mb-1rem">
                    <StyledCompanySelectionContainer>
                        {!isFieldHidden('companyName') && (
                            <div style={{ flex: '1' }}>
                                <SearchContainer index="companies">
                                    <Configure clickAnalytics filters={filters} hitsPerPage={10} />
                                    <AutocompleteCompanyNames
                                        searchInputRef={searchInputRef}
                                        name="companyName"
                                        label="Company Name"
                                        isDisabled={isFieldDisabled('companyName')}
                                        onClear={clearSearchAction}
                                        onSelect={handleSelect}
                                        required
                                    />
                                </SearchContainer>
                            </div>
                        )}
                        <StyledNewCompanyButtonContainer>
                            <div>
                                <BodySmall>OR</BodySmall>
                            </div>
                            <div>
                                <ButtonPrimaryLarge
                                    disabled={isFieldDisabled('modeButton')}
                                    onClick={() => {
                                        setModeAction(MODES.NEW_COMPANY);
                                        if (searchInputRef.current) {
                                            searchInputRef.current.value = '';
                                        }
                                    }}
                                >
                                    I Don't See My Company
                                </ButtonPrimaryLarge>
                            </div>
                        </StyledNewCompanyButtonContainer>
                    </StyledCompanySelectionContainer>

                    {touched.companyID && errors.companyID && (
                        <div>
                            <BodySmall className="mt-0.25rem text-error">{errors.companyID}</BodySmall>
                        </div>
                    )}
                </div>
            )}

            {!isFieldHidden('independentCheckbox') && (
                <StyledInputContainer>
                    <CheckboxWithoutForm
                        inline
                        checked={independent}
                        disabled={isFieldDisabled('independentCheckbox')}
                        onCheckChange={checked => {
                            toggleIndependentAction(checked);
                            if (checked) {
                                // Clears visible validation error on no longer required company fields
                                setTouched({
                                    ...touched,
                                    nonmemberCompany: false,
                                    personalResponsibility: false,
                                    title: false,
                                });
                            }
                        }}
                    >
                        Not associated with a company
                    </CheckboxWithoutForm>
                </StyledInputContainer>
            )}

            {!isFieldHidden('jobTitle') && (
                <StyledInputContainer>
                    <TextInput
                        disabled={isFieldDisabled('jobTitle')}
                        label="Job Title"
                        name="title"
                        placeholder="Job Title"
                        type="text"
                        required
                    />
                </StyledInputContainer>
            )}

            {!isFieldHidden('newCompanyFields') && (
                <AccountNewCompanyFields
                    businessTypes={businessTypes}
                    isIndependent={independent}
                    setIsManualCompanyAddress={setIsManualCompanyAddress}
                    setHasSecondaryBusinessTypes={setHasSecondaryBusinessTypes}
                />
            )}

            {!isFieldHidden('responsibility') && (
                <StyledInputContainer>
                    <SelectPersonalResponsibilities
                        isDisabled={isFieldDisabled('responsibility')}
                        label="Responsibility"
                        required
                    />
                </StyledInputContainer>
            )}
        </div>
    );
};

CompanyInformationFields.propTypes = {
    setIsManualCompanyAddress: PropTypes.func,
    setHasSecondaryBusinessTypes: PropTypes.func,
};

export default CompanyInformationFields;
